import { apiClient } from './apiClient';
import { Notification, PaginatedResponse } from '@coffeeplexity/shared';
import { mockApi } from './mockApi';
import { logger } from '../../utils/logger';

// Development mode flag - set to true when backend is not available
const USE_MOCK_API =
  process.env.NODE_ENV === 'development' && !process.env.EXPO_PUBLIC_API_URL;

const safeApiCall = async <T>(
  apiCall: () => Promise<T>,
  mockCall: () => Promise<T>
): Promise<T> => {
  if (USE_MOCK_API) {
    return mockCall();
  }

  try {
    return await apiCall();
  } catch (error: any) {
    // If API fails (CORS, 404, 429, etc.), fall back to mock
    logger.warn('API call failed, using mock data:', error.message);
    return mockCall();
  }
};

export interface NotificationPreferences {
  email_notifications: boolean;
  push_notifications: boolean;
  recipe_likes: boolean;
  recipe_comments: boolean;
  new_followers: boolean;
  creator_updates: boolean;
  system_announcements: boolean;
  marketing_emails: boolean;
}

export interface PushTokenData {
  token: string;
  platform: 'ios' | 'android';
  device_id: string;
}

export const notificationApi = {
  // Get notifications
  getNotifications: async (
    page = 1,
    limit = 20,
    unread_only = false
  ): Promise<PaginatedResponse<Notification>> => {
    const response = await apiClient.get('/notifications', {
      params: { page, limit, unread_only },
    });
    return response.data;
  },

  // Mark notification as read
  markAsRead: async (notificationId: string): Promise<void> => {
    await apiClient.put(`/notifications/${notificationId}/read`);
  },

  // Mark all notifications as read
  markAllAsRead: async (): Promise<void> => {
    await apiClient.put('/notifications/read-all');
  },

  // Delete notification
  deleteNotification: async (notificationId: string): Promise<void> => {
    await apiClient.delete(`/notifications/${notificationId}`);
  },

  // Get notification preferences
  getPreferences: async (): Promise<NotificationPreferences> => {
    return safeApiCall(
      async () => {
        const response = await apiClient.get('/notifications/preferences');
        return response.data;
      },
      () => mockApi.notifications.getPreferences()
    );
  },

  // Update notification preferences
  updatePreferences: async (
    preferences: Partial<NotificationPreferences>
  ): Promise<NotificationPreferences> => {
    return safeApiCall(
      async () => {
        const response = await apiClient.put(
          '/notifications/preferences',
          preferences
        );
        return response.data;
      },
      () => mockApi.notifications.updatePreferences(preferences)
    );
  },

  // Register push token
  registerPushToken: async (
    tokenData: PushTokenData
  ): Promise<{ message: string }> => {
    const response = await apiClient.post(
      '/notifications/push-token',
      tokenData
    );
    return response.data;
  },

  // Unregister push token
  unregisterPushToken: async (token: string): Promise<{ message: string }> => {
    const response = await apiClient.delete('/notifications/push-token', {
      data: { token },
    });
    return response.data;
  },

  // Get unread count
  getUnreadCount: async (): Promise<{ count: number }> => {
    return safeApiCall(
      async () => {
        const response = await apiClient.get('/notifications/unread-count');
        return response.data;
      },
      () => mockApi.notifications.getUnreadCount()
    );
  },
};
